import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiX,
  FiSearch,
  FiChevronUp,
  FiChevronDown,
  FiFilter,
} from 'react-icons/fi';

import {
  ActiveFilters,
  Card,
  CardHeader,
  Chip,
  EmptyState,
  FilterActions,
  FilterFieldWrap,
  FiltersBar,
  Footer,
  GhostButton,
  PageButton,
  PageEllipsis,
  Pager,
  PrimaryButton,
  SelectInput,
  SkeletonRow,
  SortButton,
  Table,
  TableWrapper,
  Td,
  TextInput,
  Th,
} from './styles';
import {
  Column,
  DataTableProps,
  FilterField,
  SortDirection,
  SortState,
} from './types';

export type { Column, FilterField, SelectOption, SortState } from './types';

function resolveKey<T>(rowKey: keyof T | ((r: T) => string), item: T): string {
  if (typeof rowKey === 'function') return rowKey(item);
  const value = item[rowKey];
  return String(value);
}

function renderCell<T>(
  column: Column<T>,
  row: T,
  index: number,
): React.ReactNode {
  if (column.render) return column.render(row, index);
  if (column.accessor) return column.accessor(row);
  const raw = (row as unknown as Record<string, unknown>)[column.key];
  if (raw === null || raw === undefined || raw === '') return '—';
  return raw as React.ReactNode;
}

function buildPages(
  current: number,
  totalPages: number,
): Array<number | 'dots'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: Array<number | 'dots'> = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(totalPages - 1, current + 1);

  if (left > 2) pages.push('dots');
  for (let p = left; p <= right; p += 1) pages.push(p);
  if (right < totalPages - 1) pages.push('dots');
  pages.push(totalPages);

  return pages;
}

function DataTable<T>({
  columns,
  data,
  rowKey,
  loading = false,
  page,
  pageSize = 25,
  total,
  onPageChange,
  filters,
  filterValues,
  onFiltersChange,
  debounceMs = 400,
  title,
  subtitle,
  headerActions,
  rowActions,
  onRowClick,
  emptyMessage = 'Nenhum registro encontrado.',
  stickyHeader,
  sort,
  onSortChange,
}: DataTableProps<T>): JSX.Element {
  const isControlled = filterValues !== undefined;

  const initialValues = useMemo<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    (filters || []).forEach(f => {
      base[f.key] = '';
    });
    return { ...base, ...(filterValues || {}) };
  }, [filters, filterValues]);

  const [localFilters, setLocalFilters] =
    useState<Record<string, string>>(initialValues);

  useEffect(() => {
    if (isControlled) {
      setLocalFilters(prev => ({ ...prev, ...(filterValues || {}) }));
    }
  }, [filterValues, isControlled]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const emit = useCallback(
    (next: Record<string, string>) => {
      if (!onFiltersChange) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const cleaned = Object.fromEntries(
          Object.entries(next).filter(([, v]) => v !== '' && v !== undefined),
        );
        onFiltersChange(cleaned);
      }, debounceMs);
    },
    [onFiltersChange, debounceMs],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const setFilterValue = useCallback(
    (field: FilterField, value: string) => {
      const masked = field.mask ? field.mask(value) : value;
      const next = { ...localFilters, [field.key]: masked };
      setLocalFilters(next);
      if (field.type === 'text') {
        emit(next);
      } else if (onFiltersChange) {
        const cleaned = Object.fromEntries(
          Object.entries(next).filter(([, v]) => v !== '' && v !== undefined),
        );
        onFiltersChange(cleaned);
      }
    },
    [emit, localFilters, onFiltersChange],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!onFiltersChange) return;
    const cleaned = Object.fromEntries(
      Object.entries(localFilters).filter(
        ([, v]) => v !== '' && v !== undefined,
      ),
    );
    onFiltersChange(cleaned);
  };

  const handleClear = () => {
    const cleared: Record<string, string> = {};
    (filters || []).forEach(f => {
      cleared[f.key] = '';
    });
    setLocalFilters(cleared);
    if (onFiltersChange) onFiltersChange({});
  };

  const handleRemoveChip = (key: string) => {
    const next = { ...localFilters, [key]: '' };
    setLocalFilters(next);
    if (onFiltersChange) {
      const cleaned = Object.fromEntries(
        Object.entries(next).filter(([, v]) => v !== '' && v !== undefined),
      );
      onFiltersChange(cleaned);
    }
  };

  const handleSort = (col: Column<T>) => {
    if (!col.sortable || !onSortChange) return;
    if (!sort || sort.key !== col.key) {
      onSortChange({ key: col.key, direction: 'asc' });
    } else if (sort.direction === 'asc') {
      onSortChange({ key: col.key, direction: 'desc' });
    } else {
      onSortChange(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagesList = buildPages(safePage, totalPages);

  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(total, safePage * pageSize);

  const activeChips = useMemo(() => {
    if (!filters) return [];
    return filters
      .map(f => {
        const value = localFilters[f.key];
        if (!value) return null;
        const display =
          f.type === 'select' || f.type === 'custom'
            ? f.options?.find(o => o.value === value)?.label || value
            : value;
        return { key: f.key, label: f.label || f.key, display };
      })
      .filter(Boolean) as Array<{
      key: string;
      label: string;
      display: string;
    }>;
  }, [filters, localFilters]);

  const colSpan = columns.length + (rowActions ? 1 : 0);

  return (
    <Card>
      {(title || subtitle || headerActions) && (
        <CardHeader>
          <div className="titles">
            {title && <h3>{title}</h3>}
            {subtitle && <span className="subtitle">{subtitle}</span>}
          </div>
          {headerActions && <div className="actions">{headerActions}</div>}
        </CardHeader>
      )}

      {filters && filters.length > 0 && (
        <FiltersBar onSubmit={handleSubmit}>
          {filters.map(field => {
            const fVal = localFilters[field.key] || '';
            let input: React.ReactNode;
            if (field.type === 'custom') {
              input = field.render?.(fVal, v => setFilterValue(field, v));
            } else if (field.type === 'select') {
              input = (
                <SelectInput
                  id={`f-${field.key}`}
                  value={fVal}
                  onChange={e => setFilterValue(field, e.target.value)}
                >
                  <option value="">{field.placeholder || 'Todos'}</option>
                  {(field.options || []).map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectInput>
              );
            } else {
              input = (
                <TextInput
                  id={`f-${field.key}`}
                  type={field.type === 'date' ? 'date' : 'text'}
                  placeholder={field.placeholder}
                  maxLength={field.maxLength}
                  value={fVal}
                  onChange={e => setFilterValue(field, e.target.value)}
                />
              );
            }
            return (
              <FilterFieldWrap key={field.key} $span={field.span}>
                {field.label && (
                  <label htmlFor={`f-${field.key}`}>{field.label}</label>
                )}
                {input}
              </FilterFieldWrap>
            );
          })}
          <FilterActions>
            <GhostButton type="button" onClick={handleClear}>
              <FiX size={14} />
              Limpar
            </GhostButton>
            <PrimaryButton type="submit">
              <FiSearch size={14} />
              Filtrar
            </PrimaryButton>
          </FilterActions>
        </FiltersBar>
      )}

      {activeChips.length > 0 && (
        <ActiveFilters>
          <FiFilter size={12} color="#94a3b8" style={{ marginTop: 4 }} />
          {activeChips.map(chip => (
            <Chip key={chip.key}>
              <span>{`${chip.label}: ${chip.display}`}</span>
              <button
                type="button"
                aria-label={`Remover filtro ${chip.label}`}
                onClick={() => handleRemoveChip(chip.key)}
              >
                <FiX size={12} />
              </button>
            </Chip>
          ))}
        </ActiveFilters>
      )}

      <TableWrapper>
        <Table $sticky={stickyHeader}>
          <thead>
            <tr>
              {columns.map(col => {
                const isSorted = sort?.key === col.key;
                const direction: SortDirection | undefined = isSorted
                  ? sort?.direction
                  : undefined;
                return (
                  <Th
                    key={col.key}
                    $align={col.align}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {(() => {
                      if (col.sortable) {
                        return (
                          <SortButton
                            type="button"
                            $active={isSorted}
                            onClick={() => handleSort(col)}
                          >
                            {col.headerRender ? col.headerRender() : col.label}
                            {direction === 'desc' ? (
                              <FiChevronDown size={12} />
                            ) : (
                              <FiChevronUp size={12} />
                            )}
                          </SortButton>
                        );
                      }
                      if (col.headerRender) return col.headerRender();
                      return col.label;
                    })()}
                  </Th>
                );
              })}
              {rowActions && <Th $align="right">Ações</Th>}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`sk-${i}`}>
                  {columns.map(col => (
                    <td key={col.key}>
                      <SkeletonRow />
                    </td>
                  ))}
                  {rowActions && (
                    <td>
                      <SkeletonRow />
                    </td>
                  )}
                </tr>
              ))}
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={colSpan}>
                  <EmptyState>
                    <div className="title">{emptyMessage}</div>
                    <div>Tente ajustar os filtros ou limpar a busca.</div>
                  </EmptyState>
                </td>
              </tr>
            )}
            {!loading &&
              data.map((row, index) => (
                <tr
                  key={resolveKey(rowKey, row)}
                  className={onRowClick ? 'clickable' : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map(col => (
                    <Td key={col.key} $align={col.align}>
                      {renderCell(col, row, index)}
                    </Td>
                  ))}
                  {rowActions && (
                    <Td
                      $align="right"
                      onClick={e => e.stopPropagation()}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {rowActions(row)}
                    </Td>
                  )}
                </tr>
              ))}
          </tbody>
        </Table>
      </TableWrapper>

      <Footer>
        <div className="count">
          {`Mostrando ${from}-${to} de ${total} ${
            total === 1 ? 'registro' : 'registros'
          }`}
        </div>
        <Pager>
          <PageButton
            type="button"
            aria-label="Primeira página"
            onClick={() => onPageChange(1)}
            disabled={safePage === 1}
          >
            <FiChevronsLeft size={14} />
          </PageButton>
          <PageButton
            type="button"
            aria-label="Página anterior"
            onClick={() => onPageChange(safePage - 1)}
            disabled={safePage === 1}
          >
            <FiChevronLeft size={14} />
          </PageButton>
          {pagesList.map((p, i) =>
            p === 'dots' ? (
              // eslint-disable-next-line react/no-array-index-key
              <PageEllipsis key={`dots-${i}`}>…</PageEllipsis>
            ) : (
              <PageButton
                key={p}
                type="button"
                $active={p === safePage}
                onClick={() => onPageChange(p)}
              >
                {p}
              </PageButton>
            ),
          )}
          <PageButton
            type="button"
            aria-label="Próxima página"
            onClick={() => onPageChange(safePage + 1)}
            disabled={safePage >= totalPages}
          >
            <FiChevronRight size={14} />
          </PageButton>
          <PageButton
            type="button"
            aria-label="Última página"
            onClick={() => onPageChange(totalPages)}
            disabled={safePage >= totalPages}
          >
            <FiChevronsRight size={14} />
          </PageButton>
        </Pager>
      </Footer>
    </Card>
  );
}

export default DataTable;
